import { getCurrentClientDbName } from "@/api/wencesApi";
import { dbNameMatchesTenant } from "@/utils/dbNameMatchesTenant";

/**
 * Los dos campos que alimentan la cuenta y que, vacíos, la dejan sin resolver. El tipo de
 * cambio no está: si se borra, la multiplicación ya lleva el importe a 0 sola.
 */
const DRIVER_KEYS = ["unit_qty", "unit_price"];
const AMOUNT_KEY = "amount";

/**
 * Nibiru: en los gastos —«Additional expenses» de la operación y el listado de gastos del
 * embarque— el importe se calcula como cantidad × precio unitario × tipo de cambio. Si el
 * usuario borra la cantidad o el precio, el importe se queda con el último valor
 * calculado, que ya no se corresponde con nada.
 *
 * No se puede resolver con una regla del modelo. El motor de cálculo mira el estado, no la
 * edición, y corre también al abrir el formulario: para él una fila con el precio vacío es
 * igual a una fila que nunca lo tuvo. Y esas existen —el flete que arma el preload del
 * embarque, las notas de crédito con importe negativo—, así que una regla «precio vacío →
 * importe vacío» les borraría el importe apenas alguien abre el embarque. Por eso la regla
 * que hay hoy deja el importe como está, y el vaciado se hace acá: solo cuando alguien
 * borra el campo de verdad.
 *
 * @param {string} path Ruta del campo que se acaba de editar, ej. "group_expenses.expenses.0.unit_qty".
 * @param {*} value Valor nuevo.
 * @returns {string|null} Ruta del importe a vaciar, o null si no corresponde.
 */
export function amountPathToClearOnEmptyUnitPrice(path, value) {
  if (typeof path !== "string" || !path) return null;
  // Vaciar es borrar el campo. Un 0 escrito a mano es un valor, no un campo vacío.
  if (!(value === null || value === undefined || value === "")) return null;

  const parts = path.split(".");
  if (parts.length < 3) return null;
  if (!DRIVER_KEYS.includes(parts[parts.length - 1])) return null;
  // Tiene que ser una fila de un grupo: ...<grupo>.<indice>.<campo>
  if (!/^\d+$/.test(parts[parts.length - 2])) return null;

  if (!dbNameMatchesTenant(getCurrentClientDbName(), "nibiru")) return null;

  return [...parts.slice(0, -1), AMOUNT_KEY].join(".");
}

/**
 * El importe se vacía con "" y no con null: el motor copia el importe sobre sí mismo
 * cuando no hay cantidad, y null lo convertiría en 0. Con "" el campo queda vacío.
 */
export const EMPTY_AMOUNT = "";
